import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './HeroSlider.css'
import * as heroImages from './heroImages'

// No photos needed: every slide has its own drawn artwork (inline SVG), so it is always
// sharp on any screen and nothing can look blurry or broken.
//
// Each slide shows a photo from /public/images/hero/ (set with `image`, embedded in heroImages.js); the drawn artwork
// below is only the fallback if a photo fails to load.
// Each slide picks an artwork with `art`:
//   'cases' | 'bank' | 'glitter' | 'repair' | 'battery'
// and which side the artwork is on with `side` ('left' | 'right').
// Colours for each slide live in HeroSlider.css (.hs__slide--cases, etc).
const DEFAULT_SLIDES = [
  {
    id: 1,
    image: heroImages.cases,
    alt: 'Pink butterfly phone case with gold detailing',
    art: 'cases',
    side: 'right',
    tone: 'dark',
    title: 'Premium cases for the iPhone 17 series',
    text: 'Leather, card-holder and armor cases in fresh colours.',
    cta: 'Shop cases',
    to: '/shop?category=Premium%20Case%20Covers',
  },
  {
    id: 2,
    image: heroImages.bank,
    alt: 'Pink MagSafe power bank with built-in stand',
    art: 'bank',
    side: 'left',
    tone: 'dark',
    title: 'MagSafe power banks',
    text: 'Snap on, charge up and go, with a built-in stand.',
    cta: 'Shop power banks',
    to: '/shop?category=Power%20Bank',
  },
  {
    id: 3,
    image: heroImages.glitter,
    alt: 'Pink glitter phone case with flower chain charm',
    art: 'glitter',
    side: 'right',
    tone: 'light',
    title: 'Clear glitter cases',
    text: 'Show off your phone with a slim, sparkling case.',
    cta: 'Shop clear cases',
    to: '/shop?category=Premium%20Case%20Covers',
  },
  {
    id: 4,
    image: heroImages.repair,
    alt: 'Phone with a cracked screen',
    art: 'repair',
    side: 'right',
    tone: 'dark',
    title: 'Cracked screen? We fix it',
    text: 'Fast display repair with doorstep pickup and delivery.',
    cta: 'Book a repair',
    to: '/#repair',
  },
  {
    id: 5,
    image: heroImages.battery,
    alt: 'Black fast-charging power bank with 100% display',
    art: 'battery',
    side: 'left',
    tone: 'dark',
    title: 'Battery and back glass replacement',
    text: 'Genuine-quality parts for Apple and Android phones.',
    cta: 'Book a repair',
    to: '/#repair',
  },
]

/* ---------- artwork (400 x 480 canvas each) ---------- */

const Gloss = ({ id, w, h, r = 36 }) => (
  <>
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#fff" stopOpacity=".22" />
        <stop offset=".45" stopColor="#fff" stopOpacity="0" />
        <stop offset="1" stopColor="#000" stopOpacity=".28" />
      </linearGradient>
    </defs>
    <rect width={w} height={h} rx={r} fill={`url(#${id})`} />
  </>
)

const CameraBar = ({ w }) => (
  <g>
    <rect x="14" y="14" width={w - 28} height="58" rx="20" fill="#000" fillOpacity=".38" stroke="#fff" strokeOpacity=".16" />
    {[0.22, 0.5, 0.78].map((p) => (
      <g key={p}>
        <circle cx={14 + (w - 28) * p} cy="43" r="14" fill="#0a0c11" stroke="#555a66" strokeWidth="2" />
        <circle cx={14 + (w - 28) * p} cy="43" r="5" fill="#1d2430" />
      </g>
    ))}
  </g>
)

const Ring = ({ cx, cy, r = 34, stroke = '#fff', o = 0.35 }) => (
  <circle cx={cx} cy={cy} r={r} fill="none" stroke={stroke} strokeOpacity={o} strokeWidth="3" />
)

function CasesArt() {
  const W = 170, H = 350
  const phone = (x, y, rot, fill, id, extra) => (
    <g transform={`translate(${x} ${y}) rotate(${rot} ${W / 2} ${H / 2})`}>
      <rect width={W} height={H} rx="36" fill={fill} />
      <Gloss id={id} w={W} h={H} />
      <rect x="9" y="9" width={W - 18} height={H - 18} rx="29" fill="none" stroke="#fff" strokeOpacity=".32" strokeDasharray="3 5" />
      <CameraBar w={W} />
      {extra}
    </g>
  )
  return (
    <svg viewBox="0 0 400 480" role="img" aria-label="Three phone cases in navy, tan and green">
      {phone(10, 80, -9, '#1b2a4c', 'c1a', <Ring cx={W / 2} cy="190" />)}
      {phone(220, 80, 9, '#2c4a3b', 'c1b', <Ring cx={W / 2} cy="190" />)}
      {phone(115, 50, 0, '#c4a075', 'c1c', (
        <g>
          <rect x="30" y="196" width="110" height="46" rx="8" fill="#f4efe6" />
          <rect x="14" y="214" width={W - 28} height="100" rx="18" fill="#8f6d45" />
          <rect x="14" y="214" width={W - 28} height="100" rx="18" fill="none" stroke="#fff" strokeOpacity=".3" strokeDasharray="3 5" />
          <Ring cx={W / 2} cy="150" r="30" stroke="#3b2a14" o=".45" />
        </g>
      ))}
    </svg>
  )
}

function BankArt() {
  const dots = [0, 1, 2, 3]
  return (
    <svg viewBox="0 0 400 480" role="img" aria-label="Orange and white MagSafe power banks with a phone">
      <g transform="translate(130 40)">
        <rect width="170" height="350" rx="36" fill="#141824" />
        <Gloss id="b2a" w={170} h={350} />
        <CameraBar w={170} />
      </g>
      {/* white bank snapped to the phone */}
      <g transform="translate(140 210)">
        <rect width="150" height="170" rx="30" fill="#f3f4f7" />
        <Gloss id="b2b" w={150} h={170} r={30} />
        {dots.map((d) => <circle key={d} cx={45 + d * 20} cy="26" r="4" fill={d < 3 ? '#35c27a' : '#c9ccd3'} />)}
        <Ring cx="75" cy="100" r="34" stroke="#9aa0ad" o=".6" />
      </g>
      {/* orange bank with lanyard */}
      <path d="M52 215 C46 150 100 150 92 215" fill="none" stroke="#ffae7a" strokeWidth="6" strokeLinecap="round" />
      <g transform="translate(10 205)">
        <rect width="126" height="250" rx="32" fill="#ff7a2f" />
        <Gloss id="b2c" w={126} h={250} r={32} />
        <rect x="46" y="14" width="34" height="12" rx="6" fill="#000" fillOpacity=".25" />
        {dots.map((d) => <circle key={d} cx={30 + d * 22} cy="48" r="4" fill={d < 2 ? '#fff' : '#ffc6a0'} />)}
        <Ring cx="63" cy="145" r="32" stroke="#6b2a00" o=".4" />
        <path d="M24 215 H102" stroke="#6b2a00" strokeOpacity=".35" strokeWidth="4" strokeLinecap="round" />
      </g>
    </svg>
  )
}

const SPARKS = [
  [60, 90, 14, '#fff'], [330, 70, 10, '#ffd9f0'], [350, 200, 16, '#fff'], [40, 250, 11, '#d9ccff'],
  [95, 400, 15, '#fff'], [310, 380, 12, '#c9f1ff'], [200, 30, 9, '#fff'], [165, 150, 8, '#fff'],
  [250, 270, 10, '#fff'], [205, 350, 8, '#ffe9b5'], [300, 130, 7, '#fff'], [120, 270, 7, '#ffd9f0'],
  [260, 440, 9, '#d9ccff'], [30, 160, 7, '#fff'], [370, 300, 8, '#fff'],
]
const spark = (s) => `M0 ${-s} L${s * 0.22} ${-s * 0.22} L${s} 0 L${s * 0.22} ${s * 0.22} L0 ${s} L${-s * 0.22} ${s * 0.22} L${-s} 0 L${-s * 0.22} ${-s * 0.22} Z`

function GlitterArt() {
  return (
    <svg viewBox="0 0 400 480" role="img" aria-label="Phone in a clear glitter case">
      <defs>
        <linearGradient id="g3" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#b79cff" />
          <stop offset=".5" stopColor="#ff9fd2" />
          <stop offset="1" stopColor="#7fd7ff" />
        </linearGradient>
      </defs>
      <g transform="translate(115 60)">
        <rect width="170" height="350" rx="36" fill="url(#g3)" />
        <CameraBar w={170} />
        <Ring cx="85" cy="190" stroke="#fff" o=".55" />
        <path d="M0 250 L120 0 H170 V40 L40 350 H0 Z" fill="#fff" fillOpacity=".12" />
        <rect x="-8" y="-8" width="186" height="366" rx="44" fill="#fff" fillOpacity=".1" stroke="#fff" strokeOpacity=".85" strokeWidth="3" />
      </g>
      {SPARKS.map(([x, y, s, c], i) => (
        <path key={i} className="hs__spark" style={{ animationDelay: `${(i % 5) * 0.55}s` }} d={spark(s)} transform={`translate(${x} ${y})`} fill={c} stroke="#8b6bff" strokeOpacity=".35" />
      ))}
    </svg>
  )
}

function RepairArt() {
  const cracks = [
    '95,160 55,95 20,55', '95,160 140,85 165,30', '95,160 150,200 168,265',
    '95,160 60,250 34,330', '95,160 112,290 100,345', '95,160 30,175 12,195', '95,160 70,125 52,128',
  ]
  return (
    <svg viewBox="0 0 400 480" role="img" aria-label="Phone with a cracked screen and a repaired tick badge">
      <g transform="translate(105 50)">
        <rect width="190" height="380" rx="40" fill="#0c0d11" stroke="#454955" strokeWidth="4" />
        <defs>
          <linearGradient id="s4" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#23396e" />
            <stop offset="1" stopColor="#0d1124" />
          </linearGradient>
        </defs>
        <rect x="10" y="10" width="170" height="360" rx="31" fill="url(#s4)" />
        <rect x="68" y="22" width="54" height="16" rx="8" fill="#000" />
        <g transform="translate(10 10)">
          <polygon points="70,138 94,128 120,145 122,178 95,192 72,180" fill="#fff" fillOpacity=".07" />
          {cracks.map((c) => <polyline key={c} points={c} fill="none" stroke="#fff" strokeOpacity=".8" strokeWidth="1.5" strokeLinejoin="round" />)}
          <polyline points="30,118 62,132 70,138" fill="none" stroke="#fff" strokeOpacity=".5" />
          <polyline points="150,210 122,192 120,178" fill="none" stroke="#fff" strokeOpacity=".5" />
        </g>
      </g>
      <circle cx="318" cy="378" r="46" fill="#ff6a5c" stroke="#121316" strokeWidth="8" />
      <path d="M296 378 L311 393 L341 360" fill="none" stroke="#1a0402" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function BatteryArt() {
  // three layers of a phone, pulled apart in an isometric view
  const iso = (ty) => `matrix(0.866 0.5 -0.866 0.5 250 ${ty})`
  const guides = [[250, 0], [362, 65], [33, 125], [146, 190]]
  return (
    <svg viewBox="0 0 400 480" role="img" aria-label="Phone pulled apart: back glass, battery and frame">
      <defs>
        <linearGradient id="g5" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f4efe1" />
          <stop offset="1" stopColor="#b9c9c0" />
        </linearGradient>
      </defs>
      {guides.map(([x, y]) => (
        <line key={x} x1={x} y1={110 + y} x2={x} y2={250 + y} stroke="#fff" strokeOpacity=".3" strokeDasharray="4 6" />
      ))}
      {/* frame / board */}
      <g transform={iso(250)}>
        <rect width="130" height="250" rx="24" fill="#26303a" stroke="#fff" strokeOpacity=".2" />
        <rect x="18" y="20" width="44" height="44" rx="8" fill="#3a4755" />
        <rect x="70" y="20" width="40" height="22" rx="6" fill="#3a4755" />
        <rect x="18" y="190" width="94" height="40" rx="8" fill="#3a4755" />
      </g>
      {/* battery */}
      <g transform={iso(180)}>
        <rect width="130" height="250" rx="24" fill="#16342b" stroke="#e8d9b0" strokeOpacity=".55" />
        <rect x="16" y="52" width="98" height="170" rx="12" fill="#1f4d3e" />
        {[84, 116, 148, 180].map((y) => <line key={y} x1="26" y1={y} x2="104" y2={y} stroke="#8fe0c0" strokeOpacity=".35" />)}
        <rect x="46" y="20" width="38" height="18" rx="5" fill="#e8d9b0" />
      </g>
      {/* back glass */}
      <g transform={iso(110)} opacity=".94">
        <rect width="130" height="250" rx="24" fill="url(#g5)" />
        <rect x="12" y="12" width="106" height="44" rx="14" fill="#10201b" fillOpacity=".4" />
        {[32, 65, 98].map((x) => <circle key={x} cx={x} cy="34" r="9" fill="#0a1411" stroke="#f4efe1" strokeOpacity=".5" strokeWidth="2" />)}
        <circle cx="65" cy="150" r="26" fill="none" stroke="#10201b" strokeOpacity=".3" strokeWidth="3" />
      </g>
    </svg>
  )
}

const ARTS = { cases: CasesArt, bank: BankArt, glitter: GlitterArt, repair: RepairArt, battery: BatteryArt }

/* ---------- real photo (falls back to the drawn artwork if the file is missing) ---------- */

function SlideArt({ slide }) {
  const [failed, setFailed] = useState(false)
  const Art = ARTS[slide.art]
  // default photo path: /images/hero/<art>.png  (e.g. /images/hero/cases.png)
  const src = slide.image ?? `/images/hero/${slide.art}.png`
  if (src && !failed) {
    return (
      <img
        className="hs__photo"
        src={src}
        alt={slide.alt || slide.title}
        decoding="async"
        onError={() => setFailed(true)}
      />
    )
  }
  return Art ? <Art /> : null
}

/* ---------- slider ---------- */

// autoplaySeconds: how long each slide stays. 0 = never slides by itself.
// Autoplay pauses on hover, keyboard focus, touch, and with the pause button.
export default function HeroSlider({ slides: slidesProp, autoplaySeconds = 4 }) {
  const slides = slidesProp || DEFAULT_SLIDES
  const count = slides.length
  const [active, setActive] = useState(0)
  const touchX = useRef(null)

  // slides keep moving one after another, back to back, forever
  useEffect(() => {
    if (count < 2 || autoplaySeconds <= 0) return undefined
    const t = setTimeout(() => setActive((a) => (a + 1) % count), autoplaySeconds * 1000)
    return () => clearTimeout(t)
  }, [active, count, autoplaySeconds])

  if (!count) return null

  const go = (i) => setActive(((i % count) + count) % count)

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX }
  const onTouchEnd = (e) => {
    const start = touchX.current
    touchX.current = null
    if (start == null) return
    const dx = e.changedTouches[0].clientX - start
    if (Math.abs(dx) > 45) go(active + (dx < 0 ? 1 : -1))
  }

  return (
    <section
      className="hs"
      data-tone={slides[active].tone || 'dark'}
      aria-roledescription="carousel"
      aria-label="Featured offers"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={() => { touchX.current = null }}
    >
      <div className="hs__stage" aria-live="off">
        {slides.map((s, i) => {
          const isActive = i === active
          return (
            <article
              key={s.id ?? i}
              className={`hs__slide hs__slide--${s.art}${s.side === 'left' ? ' is-flip' : ''}${s.banner ? ' has-banner' : ''}${isActive ? ' is-active' : ''}`}
              aria-hidden={!isActive}
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${count}`}
            >
              {s.banner && (
                <picture className="hs__banner">
                  {s.bannerMobile && <source media="(max-width: 700px)" srcSet={s.bannerMobile} />}
                  <img src={s.banner} alt="" decoding="async" loading={i === 0 ? 'eager' : 'lazy'} />
                </picture>
              )}
              <div className="hs__studio" aria-hidden="true">
                <i className="hs__spot" />
                <i className="hs__floor" />
                <i className="hs__rays" />
              </div>
              <div className="hs__grain" aria-hidden="true" />
              <div className="hs__content">
                <h2>{s.title}</h2>
                {s.text && <p>{s.text}</p>}
                {s.cta && (
                  <Link className="hs__cta" to={s.to || '/shop'} tabIndex={isActive ? 0 : -1}>
                    <span>{s.cta}</span>
                  </Link>
                )}
              </div>
              {!s.banner && (
                <div className="hs__art">
                  <div className="hs__float"><SlideArt slide={s} /></div>
                </div>
              )}
            </article>
          )
        })}
      </div>
    </section>
  )
}